import { useCallback, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import { sendOrder } from '../reducers/orderSlice';
import {
  InfoTransmissionMessage,
  messagingActions,
} from '../reducers/messagingSlice';
import {
  selectIsPosmonDisconnected,
  selectLatestOrderRequest,
} from '../selectors/order';
import { selectIsAIAutoMode } from '../redux/features/ai/ai.selector';
import {
  selectFeatureFlagOrderCheckRetry,
  selectOrderCheckTimeoutSeconds,
} from '../redux/features/config/config.selector';
import logger from '../utils/logger';

/**
 * Detects a stuck order — no check event within ORDER_CHECK_TIMEOUT_SECONDS of
 * the order event being sent — and exposes an agent-triggered retry.
 *
 * POSMON occasionally goes quiet without emitting a `disconnect` event: the
 * order goes out, no check ever comes back, and the cart lines sit at
 * SENDING/PENDING (the progress bar never turns green). Agents work around it
 * today by adding an item they don't need (typically a water) and removing it
 * to force a resend. This hook watches the latest order request — the only one
 * whose check event the orderSlice processes — and reports when its check is
 * overdue so the UI can offer a Retry button instead; `retry()` re-dispatches
 * `sendOrder`, which issues a fresh order event for the same cart, and emits a
 * METRIC event so we can quantify how often agents need it.
 *
 * The overdue clock runs per order event (from the request's orderSentAt) and
 * stops ONLY when the check event arrives. An order_status alone does not stop
 * it — deliberately, so the retry also covers orders the POS acked but never
 * finished, and orders POSLINK rejected with the 7002 "Send Order is Pending"
 * order_status error, which never get a check (reducing that error at its
 * source is HITL-1417). There is no retry cap: each retry is an explicit agent
 * action, and a retry sends a new order event whose own clock restarts.
 *
 * `isCheckOverdue` stays false when:
 * - the `order_check_retry` flag is off, or the timeout is 0;
 * - the latest request is final (the Finish Order flow has its own
 *   pending/failed handling, and POSLINK rejects resubmitted final orders);
 * - POSMON reported a disconnect (the reconnect effect in OrderReview already
 *   resends the cart when the `connect` event arrives);
 * - the AI is in auto mode (HITL never sends orders then — see sendOrder).
 */
const useOrderCheckRetry = ({
  isUnifiedMenu,
  menuUnificationOrderFailureCallback,
  menuUnificationOrderSuccessCallback,
}: {
  isUnifiedMenu: boolean;
  menuUnificationOrderFailureCallback: Function;
  menuUnificationOrderSuccessCallback: Function;
}) => {
  const dispatch = useAppDispatch();
  const isEnabled = useAppSelector(selectFeatureFlagOrderCheckRetry);
  const timeoutSeconds = useAppSelector(selectOrderCheckTimeoutSeconds);
  const latestRequest = useAppSelector(selectLatestOrderRequest);
  const isAIAutoMode = useAppSelector(selectIsAIAutoMode);
  const isPosmonDisconnected = useAppSelector(selectIsPosmonDisconnected);

  const [isCheckOverdue, setIsCheckOverdue] = useState(false);

  const isEligible = Boolean(
    isEnabled &&
      timeoutSeconds > 0 &&
      !isAIAutoMode &&
      !isPosmonDisconnected &&
      latestRequest &&
      !latestRequest.checkResponse &&
      !latestRequest.isFinal &&
      latestRequest.orderSentAt
  );

  useEffect(() => {
    if (!isEligible) {
      setIsCheckOverdue(false);
      return undefined;
    }

    // Flip to overdue the moment the check deadline passes. If we mounted /
    // re-rendered already past it (e.g. an order_status arrived late), flip
    // immediately.
    const timeoutMs = timeoutSeconds * 1000;
    const remainingMs =
      new Date(latestRequest!.orderSentAt!).getTime() + timeoutMs - Date.now();
    if (remainingMs <= 0) {
      setIsCheckOverdue(true);
      return undefined;
    }
    setIsCheckOverdue(false);
    const timerId = setTimeout(() => setIsCheckOverdue(true), remainingMs);
    return () => clearTimeout(timerId);
  }, [isEligible, timeoutSeconds, latestRequest]);

  const retry = useCallback(() => {
    const requestId = latestRequest?.id ?? 'unknown';
    const waitedMs = latestRequest?.orderSentAt
      ? Date.now() - new Date(latestRequest.orderSentAt).getTime()
      : null;
    logger.warn(
      `Order retry button clicked: no check event for request ${requestId}` +
        (waitedMs != null ? ` after ${waitedMs}ms` : '')
    );
    const payload: Partial<InfoTransmissionMessage> = {
      data: {
        message: `Order retry button clicked: no check event for request ${requestId}${
          waitedMs != null ? ` after ${waitedMs}ms` : ''
        }`,
        type: 'METRIC',
      },
    };
    dispatch(messagingActions.sendInfo(payload as any));
    dispatch(
      sendOrder({
        ...(isUnifiedMenu
          ? {
              menuUnificationOrderFailureCallback,
              menuUnificationOrderSuccessCallback,
            }
          : {}),
      })
    );
  }, [
    latestRequest,
    isUnifiedMenu,
    menuUnificationOrderFailureCallback,
    menuUnificationOrderSuccessCallback,
    dispatch,
  ]);

  return { isCheckOverdue, retry };
};

export default useOrderCheckRetry;
